import { invalidateAddressBookLists } from '@/api/address-book/set-query-data';
import { queryClient } from '@/app/providers/react-query-provider';

import { apiAddressBookFetch } from '..';
import { CONTACT_IMPORT_QUERY_KEYS } from './queries';
import { applyRowPatchesToCache, setSessionFooterCache } from './set-query-data';
import type {
  BulkSessionActionPayload,
  BulkSessionActionRes,
  BusinessCardMatchRes,
  CompleteOAuthPayload,
  ConnectProviderRes,
  ContactProviderValue,
  CreateContactImportUploadPayload,
  CreateContactImportUploadRes,
  CreateImportSessionPayload,
  CreateImportSessionRes,
  MatchBusinessCardPayload,
  MergeBusinessCardPayload,
  MergeBusinessCardRes,
  PatchSessionRowsPayload,
  PatchSessionRowsRes,
  SessionSummary,
} from './types';

// ==================== Upload Mutations ====================

/** Presigned S3 upload URL for a CSV/vCard import file or a business-card image. */
export const _createContactImportUpload = (body: CreateContactImportUploadPayload) => {
  return apiAddressBookFetch<CreateContactImportUploadRes>('/contact-import/uploads', {
    method: 'POST',
    body,
  });
};

// ==================== Session Mutations ====================

/** Create a resolution session; returns immediately with the session id (built async). */
export const _createImportSession = (body: CreateImportSessionPayload) => {
  return apiAddressBookFetch<CreateImportSessionRes>('/contact-import/sessions', {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessions] });
    },
  });
};

/** Batch per-row decision/checkbox patch (Draft only); conflicts are returned in-body. */
export const _patchImportSessionRows = ({
  id,
  body,
}: {
  id: string;
  body: PatchSessionRowsPayload;
}) => {
  return apiAddressBookFetch<PatchSessionRowsRes>(`/contact-import/sessions/${id}/rows`, {
    method: 'PATCH',
    body,
    onResponse: ({ response }) => {
      if (!response.ok) return;
      applyRowPatchesToCache(queryClient, id, body.patches, response._data as PatchSessionRowsRes);
      const refetchNeeded = body.patches.some(
        (patch) => patch.decision === null || patch.fullName !== undefined,
      );
      if (refetchNeeded) {
        queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRows, id] });
        queryClient.invalidateQueries({
          queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRowsFeed, id],
        });
      }
    },
  });
};

/** KeepRemainingAsNew / SkipRemaining over the unresolved NeedsReview rows (Draft only). */
export const _applyImportSessionBulkAction = ({
  id,
  body,
}: {
  id: string;
  body: BulkSessionActionPayload;
}) => {
  return apiAddressBookFetch<BulkSessionActionRes>(`/contact-import/sessions/${id}/bulk`, {
    method: 'POST',
    body,
    onResponse: ({ response }) => {
      // Bulk actions don't return which rowIds changed, so invalidate rather than splice cache.
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRows, id] });
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRowsFeed, id] });
      if (response.ok)
        setSessionFooterCache(queryClient, id, (response._data as BulkSessionActionRes).footer);
    },
  });
};

/** Commit the resolution session (Draft or Stopped → Committing); resumes on "Run Again". */
export const _commitImportSession = (id: string) => {
  return apiAddressBookFetch<SessionSummary>(`/contact-import/sessions/${id}/commit`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionById, id] });
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessions] });
    },
  });
};

/** Stop an in-flight commit (Committing → Stopped); already-written rows stay. */
export const _stopImportSession = (id: string) => {
  return apiAddressBookFetch<SessionSummary>(`/contact-import/sessions/${id}/stop`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionById, id] });
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessions] });
    },
  });
};

/** Discard a Draft resolution session — hard-deletes its rows and the job row. */
export const _deleteImportSession = (id: string) => {
  return apiAddressBookFetch<void>(`/contact-import/sessions/${id}`, {
    method: 'DELETE',
    onResponse: () => {
      queryClient.removeQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionById, id] });
      queryClient.removeQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRows, id] });
      queryClient.removeQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessionRowsFeed, id] });
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.sessions] });
    },
  });
};

export const _matchBusinessCard = (body: MatchBusinessCardPayload) => {
  return apiAddressBookFetch<BusinessCardMatchRes>('/contact-import/business-card/match', {
    method: 'POST',
    body,
  });
};

export const _mergeBusinessCard = (body: MergeBusinessCardPayload) => {
  return apiAddressBookFetch<MergeBusinessCardRes>('/contact-import/business-card/merge', {
    method: 'POST',
    body,
    onResponse: ({ response }) => {
      if (!response.ok) return;
      invalidateAddressBookLists(queryClient);
    },
  });
};

// ==================== Provider connection mutations ====================

/** Start (or reuse) an OAuth connection for Google/Microsoft365. Null `authorizationUrl` = reuse. */
export const _connectProvider = (provider: ContactProviderValue) => {
  return apiAddressBookFetch<ConnectProviderRes>(`/contact-import/providers/${provider}/connect`, {
    method: 'POST',
  });
};

/** Completes the OAuth code exchange server-side; call from the OAuth redirect landing page. */
export const _completeProviderOAuth = ({
  provider,
  body,
}: {
  provider: ContactProviderValue;
  body: CompleteOAuthPayload;
}) => {
  return apiAddressBookFetch<void>(`/contact-import/providers/${provider}/callback`, {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.providerConnections] });
    },
  });
};

export const _disconnectProvider = (provider: ContactProviderValue) => {
  return apiAddressBookFetch<void>(`/contact-import/providers/${provider}`, {
    method: 'DELETE',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACT_IMPORT_QUERY_KEYS.providerConnections] });
    },
  });
};
